import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import {
  DEFAULT_TERMS_AND_CONDITIONS_LOCALE,
  TERMS_AND_CONDITIONS_LOCALES,
  TERMS_AND_CONDITIONS_SLUG_MAX_LENGTH,
  TERMS_AND_CONDITIONS_SLUG_PATTERN,
  TermsAndConditionsBrand,
  isRtlTermsAndConditionsLocale,
  termsAndConditionsBrandLabel,
} from '~/consts/termsAndConditions';

import { useGetTermsAndConditions, useSaveTermsAndConditions } from '~/hooks/apis/content';

import { TermsAndConditionsSummary } from '~/services/ContentService';

import BodyPreview from './BodyPreview';
import MarkdownToolbar, { MarkdownTransform } from './MarkdownToolbar';
import { TermsAndConditionsFormValues } from './types';

const localeLabel = (locale: string) =>
  TERMS_AND_CONDITIONS_LOCALES.find((option) => option.value === locale)?.label ?? locale;

const EMPTY_FORM_VALUES: TermsAndConditionsFormValues = { slug: '', title: '', body: '' };

const EDITOR_PANE_ROWS = 20;
const EDITOR_PANE_HEADER_HEIGHT = 32;

interface Props {
  open: boolean;
  onClose: () => void;
  brand: TermsAndConditionsBrand;
  summary: TermsAndConditionsSummary | null;
  initialLocale?: string;
  existingSlugs: Array<string>;
}

export default function TermsAndConditionsEditorDialog({
  open,
  onClose,
  brand,
  summary,
  initialLocale = DEFAULT_TERMS_AND_CONDITIONS_LOCALE,
  existingSlugs,
}: Props) {
  const isCreatingSlug = !summary;
  const [locale, setLocale] = useState(initialLocale);
  const showConfirmDialog = useConfirmDialog();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<TermsAndConditionsFormValues>({ defaultValues: EMPTY_FORM_VALUES });

  const {
    data: terms,
    isLoading,
    isError,
    error,
  } = useGetTermsAndConditions(brand, summary?.slug ?? '', locale, { enabled: open && !isCreatingSlug });

  const saveMutation = useSaveTermsAndConditions();

  const hasStoredDocument = !!terms;
  const isMissingTranslation = !isCreatingSlug && !isLoading && !isError && !hasStoredDocument;

  // en-AU is the fallback every other locale relies on, so it has to exist first.
  const isDefaultLocale = locale === DEFAULT_TERMS_AND_CONDITIONS_LOCALE;
  const hasEnglishVersion = summary?.locales.includes(DEFAULT_TERMS_AND_CONDITIONS_LOCALE) ?? false;
  const isSaveBlockedWithoutEnglish = !isDefaultLocale && !hasEnglishVersion;

  const markdownFieldRef = useRef<HTMLTextAreaElement | null>(null);

  const applyMarkdown = (transform: MarkdownTransform) => {
    const textarea = markdownFieldRef.current;
    if (!textarea) {
      return;
    }

    const result = transform(textarea.value, textarea.selectionStart, textarea.selectionEnd);
    setValue('body', result.value, { shouldDirty: true });

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setLocale(initialLocale);
  }, [open, initialLocale, summary?.slug]);

  useEffect(() => {
    if (!open || isLoading) {
      return;
    }

    reset(
      terms
        ? { slug: terms.slug, title: terms.title, body: terms.body }
        : {
            ...EMPTY_FORM_VALUES,
            slug: isCreatingSlug ? getValues('slug') : (summary?.slug ?? ''),
          },
    );
  }, [open, isLoading, terms, locale, summary?.slug, isCreatingSlug, getValues, reset]);

  const isRtl = isRtlTermsAndConditionsLocale(locale);
  const rtlBoxProps = useMemo(() => (isRtl ? { dir: 'rtl' as const } : {}), [isRtl]);
  const rtlInputProps = useMemo(() => (isRtl ? { dir: 'rtl' } : {}), [isRtl]);

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await showConfirmDialog({
        title: 'Discard unsaved changes?',
        description: 'Your edits to this document have not been saved.',
      });
      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  const onSubmit = async (values: TermsAndConditionsFormValues) => {
    if (isSaveBlockedWithoutEnglish) {
      return;
    }

    const slug = isCreatingSlug ? values.slug.trim() : summary.slug;
    await saveMutation.mutateAsync({
      brand,
      slug,
      body: { locale, title: values.title.trim(), body: values.body },
    });
    onClose();
  };

  const handleLocaleChange = async (nextLocale: string) => {
    if (nextLocale === locale) {
      return;
    }

    if (isDirty) {
      const confirmed = await showConfirmDialog({
        title: 'Discard unsaved changes?',
        description: `Your edits to the ${locale} version have not been saved.`,
      });
      if (!confirmed) {
        return;
      }
    }

    setLocale(nextLocale);
  };

  const isSaving = saveMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {isCreatingSlug ? 'Create document' : summary.slug}
        <Typography variant="body2" color="text.secondary">
          {termsAndConditionsBrandLabel(brand)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            {isCreatingSlug && (
              <Controller
                name="slug"
                control={control}
                rules={{
                  required: 'Slug is required',
                  maxLength: {
                    value: TERMS_AND_CONDITIONS_SLUG_MAX_LENGTH,
                    message: `Slug must be ${TERMS_AND_CONDITIONS_SLUG_MAX_LENGTH} characters or fewer`,
                  },
                  pattern: {
                    value: TERMS_AND_CONDITIONS_SLUG_PATTERN,
                    message: 'Use lower case words separated by hyphens, e.g. booking-policies',
                  },
                  validate: (value) =>
                    !existingSlugs.includes(value.trim()) || 'That slug already exists — edit it from the list instead',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Slug"
                    required
                    sx={{ minWidth: 360 }}
                    error={!!errors.slug}
                    helperText={errors.slug?.message ?? 'Must match the slug the storefront requests'}
                  />
                )}
              />
            )}

            <TextField
              select
              label="Locale"
              value={locale}
              onChange={(event) => handleLocaleChange(event.target.value)}
              sx={{ minWidth: 260 }}
            >
              {TERMS_AND_CONDITIONS_LOCALES.map((option) => {
                const exists = summary?.locales.includes(option.value);
                const needsEnglishFirst = option.value !== DEFAULT_TERMS_AND_CONDITIONS_LOCALE && !hasEnglishVersion;
                return (
                  <MenuItem key={option.value} value={option.value} disabled={needsEnglishFirst}>
                    {option.label}
                    {needsEnglishFirst && ` — add ${DEFAULT_TERMS_AND_CONDITIONS_LOCALE} first`}
                    {!needsEnglishFirst && !isCreatingSlug && !exists && ' — not translated'}
                  </MenuItem>
                );
              })}
            </TextField>
          </Stack>

          {isError && <Alert severity="error">Failed to load this document: {error?.message ?? 'unknown error'}</Alert>}

          {isSaveBlockedWithoutEnglish && (
            <Alert severity="warning">
              This document has no {DEFAULT_TERMS_AND_CONDITIONS_LOCALE} version yet. Save that one first — every other
              locale falls back to it, so a translation on its own would never be served.
            </Alert>
          )}

          {!isSaveBlockedWithoutEnglish && isMissingTranslation && (
            <Alert severity="info">
              No {localeLabel(locale)} version exists — saving creates one. The fields are blank deliberately: the{' '}
              {DEFAULT_TERMS_AND_CONDITIONS_LOCALE} fallback is not a translation to edit.
            </Alert>
          )}

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Controller
                name="title"
                control={control}
                rules={{ validate: (value) => !!value.trim() || 'Title is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    required
                    fullWidth
                    slotProps={{ htmlInput: rtlInputProps }}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ height: EDITOR_PANE_HEADER_HEIGHT }}
                    >
                      <Typography variant="subtitle2">Markdown</Typography>
                      <MarkdownToolbar onApply={applyMarkdown} disabled={isSaving} />
                    </Stack>
                    <Controller
                      name="body"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          inputRef={markdownFieldRef}
                          fullWidth
                          multiline
                          minRows={EDITOR_PANE_ROWS}
                          maxRows={EDITOR_PANE_ROWS}
                          slotProps={{
                            htmlInput: {
                              ...rtlInputProps,
                              style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 },
                            },
                          }}
                        />
                      )}
                    />
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={1} height="100%">
                    <Stack direction="row" alignItems="center" sx={{ height: EDITOR_PANE_HEADER_HEIGHT }}>
                      <Typography variant="subtitle2">Preview</Typography>
                    </Stack>
                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                      <Box
                        {...rtlBoxProps}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          px: 2,
                          py: 1,
                          overflowY: 'auto',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <BodyPreview control={control} />
                      </Box>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading || isSaving || isSaveBlockedWithoutEnglish}
        >
          Save
        </Button>
        <Button variant="text" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
